import ExpandMore from "@mui/icons-material/ExpandMore"
import { TextField, TextFieldProps } from "@mui/material"
import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import FormHelperText from "@mui/material/FormHelperText"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"

import React, { ChangeEvent, FC, useState } from "react"

import { create } from "src/helpers/bem"
import { TranslationMessages } from "src/translations"

import styles from "./Select.module.scss"

const bem = create(styles, "Select")

type SelectClassNamesProps = {
  root?: string
  icon?: string
  selectedItem?: string
  item?: string
  wrapper?: string
}

export type SelectOption = {
  onclick?: (event?: any) => void
  key: string | number
  value: string
  disabled?: boolean
  description?: string
  helperText?: string
}

export type SelectProps = TextFieldProps & {
  errorText?: string
  helperText?: string
  messages?: TranslationMessages
  items?: SelectOption[]
  classes?: SelectClassNamesProps
  onValidate?: (error?: string) => void
  multiple?: boolean
  sizeVariant?: "small" | "medium" | "large"
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

export const Select: FC<SelectProps> = ({
  multiple,
  items = [],
  variant = "standard",
  value,
  required,
  classes,
  errorText,
  messages,
  helperText,
  onChange,
  onValidate,
  sizeVariant = "large",
  ...props
}) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const handleValidation = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value = e?.target?.value as string | string[]
    if (!onValidate) {
      return null
    }
    if (required && value?.length === 0) {
      return onValidate(
        messages?.components?.common?.fields?.required ??
          "This field is required!",
      )
    }
    return onValidate()
  }
  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl
        className={bem(undefined, sizeVariant, classes?.root)}
        error={!!errorText}
        required={required}
      >
        <TextField
          {...props}
          select
          className={bem("textfield", undefined, classes?.selectedItem)}
          InputLabelProps={{ shrink: true }}
          label={<span className={bem("label")}>{props?.label}</span>}
          required={required}
          variant={variant}
          InputProps={{
            inputProps: { className: bem("input") },
            disableUnderline: true,
          }}
          SelectProps={{
            MenuProps: {
              slotProps: {
                paper: {
                  className: bem("wrapper", sizeVariant, classes?.wrapper),
                },
              },
              MenuListProps: {
                className: bem("menu"),
              },
            },
            className: bem("select", expanded ? "expanded" : ""),
            multiple: multiple,
            onOpen: () => {
              setExpanded(true)
            },
            onClose: () => {
              setExpanded(false)
            },
            IconComponent: ExpandMore,
            renderValue: (value) => {
              if (multiple) {
                const valueList = value as string[]
                return valueList.map((item, index) => (
                  <>
                    {items?.find((el) => el.key === item)?.value}
                    {index === valueList.length - 1 ? "" : ", "}
                  </>
                ))
              } else return <>{items?.find((el) => el.key === value)?.value}</>
            },
          }}
          value={
            multiple
              ? Array.isArray(value)
                ? value.map((v) =>
                    typeof v === "object" && "key" in v ? v.key : v,
                  ) // Extract keys if the object contains a key
                : []
              : typeof value === "object" && value && "key" in value
                ? value.key // Extract the key if the object contains a key
                : (items?.find((el) => el.key === value)?.key ?? "")
          }
          onChange={(event) => {
            handleValidation(event)
            onChange?.(event)
          }}
        >
          {items && items?.length > 0
            ? items.map((item) => {
                if (multiple) {
                  return (
                    <MenuItem
                      key={item.key}
                      className={bem("menuitem")}
                      disabled={item.disabled}
                      value={item.key}
                    >
                      <Checkbox
                        checked={
                          Array.isArray(value)
                            ? value.includes(item.key)
                            : false
                        }
                      />
                      <ListItemText primary={item.value} />
                    </MenuItem>
                  )
                }
                return (
                  <MenuItem
                    key={item?.key}
                    className={bem("menuitem")}
                    disabled={item.disabled}
                    value={item.key}
                    onClick={item.onclick}
                  >
                    <div className={bem("menu-item__text")}>
                      <p className={bem("item__title")}>{item.value}</p>
                      {item.description && (
                        <span className={bem("item__description")}>
                          {item.description}
                        </span>
                      )}
                    </div>
                  </MenuItem>
                )
              })
            : null}
        </TextField>
        {errorText || helperText ? (
          <FormHelperText>{errorText ?? helperText}</FormHelperText>
        ) : null}
        {!errorText || helperText ? (
          <FormHelperText>
            {items?.find((e) => e.key === value)?.helperText ?? ""}
          </FormHelperText>
        ) : null}
      </FormControl>
    </Box>
  )
}
